import {Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {PAGES_AND_PATHS} from '../../../constants/pages-and-paths';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationHttpService} from '../../../services/http-services/authentication/authentication-http.service';
import {AuthenticationHelperService} from "../../../services/helpers/authentication-helper.service";
import {Countries, PhoneRegex} from "../../../constants/general-variables";
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'tl-verify-account',
  templateUrl: './verify-account.component.html',
})
export class VerifyAccountComponent implements OnInit {
  codeForm!: FormGroup;
  codeArray = Array.from({ length: 5 }, (_, i) => i);
  isCodeError = false;
  isSpinnerActive = false;
  numberOfSecondsToEnableResendCodeBtn = 0;
  counterInterval!: ReturnType<typeof setInterval>;
  isVerificationCompanyAccount = false;
  selectedArea = Countries[230];
  showChangePhone = false;
  isAreaCodeOpen = false;
  formTriedToSubmit = false;
  historyState: any;
  @ViewChildren('codeInput') inputElements!: QueryList<ElementRef>;
  protected readonly PAGES_AND_PATHS = PAGES_AND_PATHS;
  protected readonly Countries = Countries;
  protected readonly history = history;
  constructor(private fb: FormBuilder,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private toastService: ToastrService,
              protected authHelperService: AuthenticationHelperService,
              private authHttpService: AuthenticationHttpService) {
  }

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.url.map(segment => segment.path).join('/') === PAGES_AND_PATHS.verifyAccountCompany.pageInRouting) {
      this.isVerificationCompanyAccount = true;
    }

    this.historyState = history.state;

    this.buildForm();
    this.buildFormChangePhone();
    this.startCounter();
  }

  buildForm(): void {
    this.codeForm = this.fb.group({});
    this.codeArray.forEach((_, i) => {
      this.codeForm.addControl(`code${i}`, this.fb.control('', [Validators.required, Validators.pattern('[0-9]')]));
    });

    if (this.showChangePhone) {
      this.codeForm.controls['phone_number'].valueChanges.subscribe((value) => {
        const trimmedValue = value.trim();
        if (value !== trimmedValue) {
          this.codeForm.controls['phone_number'].setValue(trimmedValue, {
            emitEvent: false,
          });
        }
      });
    }

    this.onSelectCodeArea(
      Countries.find(
        (x) =>
          x.phone_code === this.authHelperService.getUserPhone.split(' ')[0]
      )
    );
  }

  buildFormChangePhone(): void {
    this.codeForm.addControl(
      'phone_number',
      this.fb.control('', [Validators.pattern(PhoneRegex)])
    );
  }

  onInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement
    this.isCodeError = false;
    if (target.value && index < this.codeArray.length - 1) {
      const nextInput = this.inputElements.toArray()[index + 1].nativeElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    this.isCodeError = false;
    if (event.key === 'Backspace' && index > 0 && !this.codeForm.controls[`code${index}`].value) {
      const previousInput = this.inputElements.toArray()[index - 1].nativeElement;
      if (previousInput) {
        previousInput.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pasteData = event.clipboardData?.getData('text');
    if (pasteData && pasteData.length === this.codeArray.length) {
      pasteData.split('').forEach((char, i) => {
        this.codeForm.controls[`code${i}`].setValue(char);
      });
      this.isCodeError = false;
    }
    event.preventDefault();
  }

  onSubmit(): void {
    this.isCodeError = false;
    if (! this.historyState.hasOwnProperty('phoneNumber')) {
      if (this.codeForm.valid) {
        this.isSpinnerActive = true;
        const phone = this.codeForm.controls['phone_number'].value;
        const code = Object.values(this.codeForm.value)
          .join('')
          .replace(phone, '');

        this.authHttpService.postVerifyAccount({code}).then(res => {
          this.isSpinnerActive = false;
          if (res.verified_successfully) {
            this.authHelperService.setUserData({...this.authHelperService.getUserData, email_verified_at: true});
            if (this.historyState.hasOwnProperty('navigateTopics')) {
              this.router.navigate([PAGES_AND_PATHS.selectTopics.pagePath]);
            } else {
              this.isVerificationCompanyAccount ? this.router.navigate([PAGES_AND_PATHS.companySubscription.pagePath]) : this.router.navigate([PAGES_AND_PATHS.completeRegistration.pagePath]);
            }
          } else {
            this.isCodeError = true;
          }
        }).catch(error => {
          this.isCodeError = true;
          this.isSpinnerActive = false;
        });
      } else {
        this.isCodeError = true;
      }
    } else {
      this.isSpinnerActive = true;
      const code = Object.values(this.codeForm.value)
        .join('')
        .replace(this.historyState, '');
      this.authHttpService
        .postLoginWithCode({code, phone_number: this.historyState?.phoneNumber })
        .then((res: any) => {
          this.isSpinnerActive = false;
          this.authHelperService.clientLogin(
            res.data.user,
            res.data.auth_token,
            PAGES_AND_PATHS.home.pagePath
          );
        })
        .catch((error: any) => {
          this.isCodeError = true;
          this.isSpinnerActive = false;
        });
    }
  }

  resendVerificationCode(): void {
    if (!this.numberOfSecondsToEnableResendCodeBtn) {
      this.authHttpService.postResendVerificationCode().then(res => {
        this.startCounter();
      });
    }
  }

  startCounter(): void {
    this.numberOfSecondsToEnableResendCodeBtn = 60;
    this.counterInterval = setInterval(() => {
      (this.numberOfSecondsToEnableResendCodeBtn)
        ? this.numberOfSecondsToEnableResendCodeBtn--
        : clearInterval(this.counterInterval);
    }, 1000);
  }

  onSelectCodeArea(country: any): void {
    this.selectedArea = country;
    this.isAreaCodeOpen = false;
  }

  onSubmitChangePhone(): void {
    this.formTriedToSubmit = true;
    this.isSpinnerActive = true;
    if (this.authHelperService.isAuthenticated) {
      this.authHttpService
        .postCreateNumberRegister({
          phone_number:
            this.selectedArea.phone_code +
            ' ' +
            this.codeForm.controls['phone_number'].value,
        })
        .then((res: any) => {
          this.isSpinnerActive = false;
          this.showChangePhone = false;
          this.authHelperService.setUserData({
            ...this.authHelperService.getUserData,
            phone_number:
              this.selectedArea.phone_code +
              ' ' +
              this.codeForm.controls['phone_number'].value,
          });
        })
        .catch(
          ({ error }: { error: { errors: { [key: string]: string } } }) => {
            this.isSpinnerActive = false;
            Object.values(error.errors).forEach((error) =>
              this.toastService.error(error[0])
            );
          }
        );
    } else {
      const fullNumber =
        this.selectedArea.phone_code +
        ' ' +
        this.codeForm.controls['phone_number'].value;
      this.authHttpService
        .postCreateNumberRegisterPostCall({
          phone_number: fullNumber,
        })
        .then((res: any) => {
          this.isSpinnerActive = false;
          this.showChangePhone = false;
          this.authHelperService.setUserData({
            ...this.authHelperService.getUserData,
            phone_number: fullNumber,
          });
        })
        .catch(
          ({ error }: { error: { errors: { [key: string]: string } } }) => {
            this.isSpinnerActive = false;
            Object.values(error.errors).forEach((error) =>
              this.toastService.error(error[0])
            );
          }
        );
    }
  }
}
